import { useState } from 'react';
import { Button, Input } from '../../../design-system';
import { fetchCaptcha, verifyGstin, mapTaxpayerToGstin } from '../../../lib/gstPortal';

// Inline GST-portal lookup panel — rendered in place inside the GSTINs section
// (not a modal), so the fetched row lands right where the user is working.
//
// Calls the live GST API directly (its CORS allow-list covers localhost:3000).
// Two steps, because the portal shows a captcha a human must read:
//   1. /gst/captcha { gstin }              -> sessionId + captcha image
//   2. /gst/verify  { sessionId, captcha } -> taxpayer record
// The session is single-use — it's consumed on verify whether or not the
// captcha was right, so any retry needs a fresh captcha (hence `resetCaptcha`).
//
// `initialGstin` / `lockGstin` serve the REFRESH case — re-pulling a GSTIN the
// firm already holds. The number is not in question there: the point is what
// the portal says about it TODAY (a registration since cancelled or suspended,
// a changed jurisdiction or address), so it is filled in and made read-only
// rather than retyped and possibly mistyped into a different taxpayer.
function GstPortalFetch({ onFetched, onCancel, initialGstin = '', lockGstin = false }) {
  const [gstin, setGstin] = useState(String(initialGstin || '').toUpperCase());
  const [sessionId, setSessionId] = useState('');
  const [captchaImage, setCaptchaImage] = useState('');
  const [captcha, setCaptcha] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const resetCaptcha = () => {
    setSessionId('');
    setCaptchaImage('');
    setCaptcha('');
  };

  const loadCaptcha = async () => {
    if (!/^[0-9A-Z]{15}$/.test(gstin.trim().toUpperCase())) {
      setError('Enter a valid 15-character GSTIN');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await fetchCaptcha(gstin);
      if (!data.captcha || !data.sessionId) {
        setError(data.message || data.msg || 'The portal did not return a captcha. Try again.');
        return;
      }
      setSessionId(data.sessionId);
      setCaptchaImage(data.captcha);
      setCaptcha('');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not reach the GST portal');
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    if (!captcha.trim()) {
      setError('Enter the captcha shown above');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await verifyGstin(sessionId, captcha);

      // A wrong captcha / unknown GSTIN still comes back HTTP 200, with a
      // message instead of the taxpayer fields — trust nothing unless `sts`
      // is present. Surface whatever the portal actually said.
      if (!data || !data.sts) {
        // eslint-disable-next-line no-console
        console.warn('[GST verify] unexpected response:', data);
        setError(data?.message || data?.msg || data?.error || 'Verification failed — the captcha may be wrong. Try a new captcha.');
        resetCaptcha();
        return;
      }

      const { gstin: mapped, suggested } = mapTaxpayerToGstin(data);
      onFetched(mapped, suggested);
      setGstin('');
      resetCaptcha();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Verification failed');
      resetCaptcha();
    } finally {
      setLoading(false);
    }
  };

  // Enter should advance the current step, never submit the parent client form.
  const onKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (captchaImage) verify();
    else loadCaptcha();
  };

  return (
    <div className="gst-fetch-panel">
      <div className="gst-fetch-head">
        <span className="ds-field-label">Fetch from GST Portal</span>
        <button type="button" className="btn-remove-icon" onClick={onCancel} aria-label="Close">×</button>
      </div>

      {error && <div className="form-error" role="alert">{error}</div>}

      <div className="form-row" style={{ alignItems: 'flex-end' }}>
        <Input
          label="GSTIN *"
          value={gstin}
          onChange={(e) => setGstin(e.target.value.toUpperCase())}
          onKeyDown={onKeyDown}
          maxLength={15}
          placeholder="27AAAAA0000A1Z5"
          // Locked on a refresh: changing it would silently pull a DIFFERENT
          // taxpayer's record over the row being edited.
          readOnly={lockGstin}
          disabled={loading || !!captchaImage || lockGstin}
        />
        {!captchaImage && (
          <Button type="button" variant="primary" onClick={loadCaptcha} loading={loading}>
            Get Captcha
          </Button>
        )}
      </div>

      {captchaImage && (
        <>
          <div className="gst-captcha-row">
            <img src={captchaImage} alt="GST portal captcha" className="gst-captcha-img" />
            <Button type="button" variant="secondary" size="sm" onClick={loadCaptcha} disabled={loading}>
              ↻ New captcha
            </Button>
          </div>
          <div className="form-row" style={{ alignItems: 'flex-end' }}>
            <Input
              label="Enter the captcha *"
              value={captcha}
              onChange={(e) => setCaptcha(e.target.value)}
              onKeyDown={onKeyDown}
              autoFocus
              disabled={loading}
            />
            <Button type="button" variant="primary" onClick={verify} loading={loading}>
              Verify &amp; Fill
            </Button>
          </div>
        </>
      )}

      {!captchaImage && (
        <p className="hint-text">
          {lockGstin
            ? 'Re-reads this registration from the portal — status, jurisdictions and address as'
              + ' they stand today. Nothing is saved until you review it and click Save.'
            : 'Pulls the legal name, address, registration type, status and jurisdictions straight'
              + ' from the portal. Everything stays editable afterwards.'}
        </p>
      )}
    </div>
  );
}

export default GstPortalFetch;
